'use client';

import { useCallback, useEffect, useRef } from 'react';

export type DebouncedCallback<TArgs extends unknown[]> = ((...args: TArgs) => void) & {
  cancel: () => void;
  flush: () => void;
  isScheduled: () => boolean;
};

/**
 * Returns a stable debounced function with `cancel` and `flush`.
 * Pending calls are flushed on unmount so quantity edits are not dropped.
 */
export function useDebouncedCallback<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => TReturn,
  delay: number,
): DebouncedCallback<TArgs> {
  const fnRef = useRef(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgsRef = useRef<TArgs | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingArgsRef.current = null;
  }, []);

  const flush = useCallback(() => {
    if (!pendingArgsRef.current) {
      return;
    }
    const args = pendingArgsRef.current;
    pendingArgsRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    fnRef.current(...args);
  }, []);

  const run = useCallback(
    (...args: TArgs) => {
      pendingArgsRef.current = args;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const pending = pendingArgsRef.current;
        pendingArgsRef.current = null;
        if (pending) {
          fnRef.current(...pending);
        }
      }, delay);
    },
    [delay],
  ) as DebouncedCallback<TArgs>;

  run.cancel = cancel;
  run.flush = flush;
  run.isScheduled = () => timerRef.current !== null;

  useEffect(
    () => () => {
      run.flush();
    },
    [run],
  );

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [delay]);

  return run;
}
